import { type ReactElement } from 'react';
import { Button } from '@flos/react-ui';

import { AuthenticatedTemplate, UnauthenticatedTemplate, useAppContext } from 'context/AppContext';
import { useConfigContext } from 'context/ConfigContext';

import logoImg from 'assets/images/td-logo.svg';

import styles from './AppLayout.module.css';

/* ============================================ Local Types ============================================ */
type TLoggedInLayout = {
  onClickLogout: () => void;
  children: ReactElement;
};

type TLoginLayout = {
  appName: string;
  onClickLogin: () => void;
};

type TAppLayout = {
  children: ReactElement;
};

/* ============================================ Local Components ============================================ */
const LogInLayout = ({ appName, onClickLogin }: TLoginLayout) => {
  return (
    <UnauthenticatedTemplate>
      <div className={styles['log-in-root']}>
        <img width="300px" src={logoImg} alt="topdanmark-logo" loading="lazy" />
        <div className={styles['log-in-content']}>You are required to login to access {appName}</div>
        <Button theme="primary" onClick={onClickLogin}>
          LOGIN
        </Button>
      </div>
    </UnauthenticatedTemplate>
  );
};

const LoggedInLayout = ({ onClickLogout, children }: TLoggedInLayout) => {
  return (
    <AuthenticatedTemplate>
      <div>
        <div className={styles['logged-in-header']}>
          <div>
            <img width="300px" src={logoImg} alt="topdanmark-logo" loading="lazy" />
          </div>
          <div className={styles['logged-in-header-right']}>
            <Button className={styles['logged-in-header-button']} theme="secondary" onClick={onClickLogout}>
              LOGOUT
            </Button>
          </div>
        </div>
        {children}
      </div>
    </AuthenticatedTemplate>
  );
};

/* ============================================ Main Component ============================================ */
const AppLayout = ({ children }: TAppLayout) => {
  const { appName } = useConfigContext();
  const {
    msal: { onLogin, onLogout }
  } = useAppContext();

  return (
    <div className={styles['root']}>
      <LogInLayout appName={appName} onClickLogin={onLogin} />
      <LoggedInLayout onClickLogout={onLogout}>{children}</LoggedInLayout>
    </div>
  );
};

export default AppLayout;
